import { SetURLSearchParams } from "react-router-dom";
import { z } from "zod";
import { deleteEmptyFieldsFromObject } from "@shared/lib/utils";

export const DEFAULT_SORT_KEY = "id";
export const DEFAULT_SORT_METHOD: "ASC" | "DESC" = "DESC";

type CustomDataFilterItem = {
  field_index: number;
  field_value: string;
};

export type ParsedBrowseUrl<TFilters> = {
  filters: Partial<TFilters>;
  sortKey: string;
  sortMethod: "ASC" | "DESC";
};

function parseCustomData(raw: string | null): CustomDataFilterItem[] | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      const items: CustomDataFilterItem[] = [];

      for (const [key, val] of Object.entries(parsed)) {
        if (typeof val !== "string" || val.trim() === "") {
          continue;
        }

        const idx = Number(key);

        if (!Number.isInteger(idx) || idx < 0) {
          continue;
        }

        items.push({ field_index: idx, field_value: val });
      }

      items.sort((a, b) => a.field_index - b.field_index);
      return items.length > 0 ? items : undefined;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

function serializeCustomData(
  customDataArray: { field_index?: number | null; field_value?: string | null }[],
): string | undefined {
  const obj: Record<string, string> = {};

  customDataArray.forEach((item, i) => {
    const value = typeof item.field_value === "string" ? item.field_value.trim() : "";

    if (value === "") {
      return;
    }

    const idx =
      item.field_index !== undefined && item.field_index !== null
        ? Number(item.field_index)
        : i;

    if (!Number.isInteger(idx) || idx < 0) {
      return;
    }

    obj[String(idx)] = value;
  });

  return Object.keys(obj).length > 0 ? JSON.stringify(obj) : undefined;
}

type CreateBrowseUrlParamsOptions<TFilters> = {
  omitFromUrl?: (keyof TFilters)[];
};

/**
 * Creates parse/sync helpers for browse page URL query params from a Zod search schema.
 */
export function createBrowseUrlParams<TFilters extends Record<string, unknown>>(
  schema: z.ZodObject<z.ZodRawShape>,
  options: CreateBrowseUrlParamsOptions<TFilters> = {},
) {
  const filterParamKeys = Object.keys(schema.shape).filter(
    (key) => !options.omitFromUrl?.includes(key as keyof TFilters),
  );

  function parseBrowseUrlParams(searchParams: URLSearchParams): ParsedBrowseUrl<TFilters> {
    const param = (key: string) => searchParams.get(key) ?? undefined;
    const filters: Partial<TFilters> = {};

    for (const key of filterParamKeys) {
      const value = param(key);

      if (value !== undefined) {
        (filters as Record<string, string>)[key] = value;
      }
    }

    const customData = parseCustomData(searchParams.get("custom_data"));

    if (customData) {
      (filters as { custom_data?: CustomDataFilterItem[] }).custom_data = customData;
    }

    const sortKey = param("sort")?.replace(/^-/, "") ?? DEFAULT_SORT_KEY;
    const rawOrder = param("order")?.toUpperCase();
    const sortMethod =
      rawOrder === "ASC" || rawOrder === "DESC" ? rawOrder : DEFAULT_SORT_METHOD;

    return { filters, sortKey, sortMethod };
  }

  function syncBrowseUrlParams(
    setSearchParams: SetURLSearchParams,
    data: TFilters,
    sortKey: string,
    sortMethod: "ASC" | "DESC",
  ): void {
    const params = new URLSearchParams();
    const cleaned = deleteEmptyFieldsFromObject(data) as TFilters;

    for (const key of filterParamKeys) {
      const value = cleaned[key as keyof TFilters];

      if (value === undefined || value === null) {
        continue;
      }

      if (typeof value === "boolean") {
        params.set(key, value ? "true" : "false");
      } else {
        params.set(key, String(value));
      }
    }

    const customDataValue = (cleaned as { custom_data?: { field_index?: number | null; field_value?: string | null }[] })
      .custom_data;

    const customDataParam =
      customDataValue?.length ? serializeCustomData(customDataValue) : undefined;

    if (customDataParam) {
      params.set("custom_data", customDataParam);
    }

    const bareSortKey = sortKey.replace(/^-/, "");

    if (bareSortKey !== DEFAULT_SORT_KEY) {
      params.set("sort", bareSortKey);
    }

    if (sortMethod !== DEFAULT_SORT_METHOD) {
      params.set("order", sortMethod);
    }

    setSearchParams(params, { replace: true });
  }

  return { parseBrowseUrlParams, syncBrowseUrlParams };
}
